import { useMemo } from 'react'

import { CHAIN_INFO } from '@cowprotocol/common-const'
import { useIsBridgingEnabled } from '@cowprotocol/common-hooks'
import { SupportedChainId } from '@cowprotocol/cow-sdk'
import { useWalletInfo, useNetworkSwitchUnsupported } from '@cowprotocol/wallet'

import { useBridgeSupportedNetworks, useRoutesAvailability } from 'entities/bridgeProvider'

import { Field } from 'legacy/state/types'

import { TradeType } from 'common/modules/tradeNavigation'

import { useSelectTokenWidgetState } from './useSelectTokenWidgetState'
import { useSupportedChains } from './useSupportedChains'
import { useSupportedTargetChains } from './useSupportedTargetChains'

import { ChainsToSelectState } from '../types'
import { createOutputChainsState } from '../utils/chainsState'
import { mapChainInfo } from '../utils/mapChainInfo'
import { sortChainsByDisplayOrder } from '../utils/sortChainsByDisplayOrder'

// Re-export for tests and external usage
export { createInputChainsState, createOutputChainsState } from '../utils/chainsState'

/**
 * Returns an array of chains to select in the token selector widget.
 * The array depends on sell/buy token selection.
 * For the sell token we return all supported chains.
 * For the buy token we return all app-supported chains with disabled state for non-bridgeable targets.
 * based on runtime checks (swap route + wallet compatibility).
 */
export function useChainsToSelect(): ChainsToSelectState | undefined {
  const { chainId } = useWalletInfo()
  const { field, selectedTargetChainId = chainId, tradeType, oppositeToken } = useSelectTokenWidgetState()
  const { data: bridgeSupportedNetworks, isLoading } = useBridgeSupportedNetworks()
  const isBridgingEnabled = useIsBridgingEnabled() // Reads from Jotai atom
  const isAdvancedTradeType = tradeType === TradeType.LIMIT_ORDER || tradeType === TradeType.ADVANCED_ORDERS
  const shouldHideNetworkSelector = useNetworkSwitchUnsupported()

  const supportedChains = useSupportedChains()
  const supportedTargetChains = useSupportedTargetChains()

  // When selecting the BUY token, the bridge "source chain" is the SELL token's chain (oppositeToken),
  // not necessarily the wallet network. This keeps chain availability accurate when wallet network != trade network.
  const sourceChainId =
    field === Field.OUTPUT && oppositeToken?.chainId ? (oppositeToken.chainId as SupportedChainId) : chainId

  const destinationChainIds = useMemo(() => supportedTargetChains.map((c) => c.id), [supportedTargetChains])
  const isBuyField = field === Field.OUTPUT
  const routesAvailability = useRoutesAvailability(
    isBuyField && isBridgingEnabled ? sourceChainId : undefined,
    destinationChainIds,
  )

  return useMemo(() => {
    // TODO: Limit/TWAP orders currently disable chain selection; revisit when SC wallet bridging supports advanced trades.
    if (!field || !chainId || !sourceChainId || !isBridgingEnabled || isAdvancedTradeType) return undefined

    const chainInfo = CHAIN_INFO[sourceChainId]
    if (!chainInfo) return undefined

    if (field === Field.INPUT) {
      return {
        defaultChainId: selectedTargetChainId,
        chains: shouldHideNetworkSelector ? [] : sortChainsByDisplayOrder(supportedChains),
        isLoading: false,
      }
    }

    // BUY token selection - include disabled chains info
    return createOutputChainsState({
      selectedTargetChainId,
      chainId: sourceChainId,
      currentChainInfo: mapChainInfo(sourceChainId, chainInfo),
      bridgeSupportedNetworks,
      supportedChains: supportedTargetChains,
      isLoading,
      routesAvailability,
    })
  }, [
    field,
    selectedTargetChainId,
    chainId,
    sourceChainId,
    bridgeSupportedNetworks,
    supportedChains,
    supportedTargetChains,
    isLoading,
    isBridgingEnabled,
    isAdvancedTradeType,
    routesAvailability,
    shouldHideNetworkSelector,
  ])
}
